<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcBillingListItemHeader',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'deleted']);

  const props = defineProps({
    ...userComponentProps,
    item: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const messages = ref({
    newItem: $fn.value.pgettext('BillingListItem', 'New item'),
    editItem: $fn.value.pgettext('BillingListItem', 'Edit item'),
  });
  const ux = ref([]);

  const $data = computed(() => ({
    messages: messages.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'messages' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { messages, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const resourceTitle = computed(() => {
    return $fn.value.pgettext('BillingListItem', 'Billing list item');
  });
  const resourceKey = computed(() => {
    if (loaded.value) {
      return props.item.isNew
        ? messages.value.newItem
        : messages.value.editItem;
    }
    return null;
  });

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );

  const act_1040167445267876_emit_241vif44v_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_3601807637515735_emit_fdqvvm70q_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_3601807637515735_reloadGrid_xu247o945_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_3601807637515735_request_eu649s4m2_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_register_item',
      data: {
        id: props.item.id,
        etag: props.item.etag,
        locked: true,
      },
    };
  };
  const act_4139696446929656_emit_374fwerci_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_4139696446929656_reloadGrid_g3jyxfmoy_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_4139696446929656_request_vsyu04aot_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_register_item',
      data: {
        id: props.item.id,
        etag: props.item.etag,
        locked: false,
      },
    };
  };
  const act_4700643919467772_emit_9cp7ht9tm_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_4700643919467772_reloadGrid_ibky6dfrw_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_4700643919467772_request_brv7hyjek_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_register_item',
      data: {
        id: props.item.id,
        etag: props.item.etag,
        void: true,
      },
    };
  };
  const act_295664594794476_emit_842ors22r_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_295664594794476_reloadGrid_z1f48osl7_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_295664594794476_request_50581xg8r_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_register_item',
      data: {
        id: props.item.id,
        etag: props.item.etag,
        void: false,
      },
    };
  };
  const act_8474446275270171_emit_oqz2pdo6d_getActionArgs = async ($event) => {
    return {
      event: 'deleted',
    };
  };

  const act_1040167445267876_emit_241vif44v = new actionTypes.EmitAction({
    actionArgs: act_1040167445267876_emit_241vif44v_getActionArgs,
    displayName: 'reload',
  });
  const inst_evh_1040167445267876_clickPreventStop_cxlrqnx6w =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_emit_241vif44v,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_1040167445267876_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1040167445267876_clickPreventStop_cxlrqnx6w],
      event: 'click.prevent.stop',
    });
  const act_3601807637515735_emit_fdqvvm70q = new actionTypes.EmitAction({
    actionArgs: act_3601807637515735_emit_fdqvvm70q_getActionArgs,
  });
  const inst_evh_3601807637515735_success_9ttk1f1d6 =
    new actionTypes.EventHandlerImpl({
      action: act_3601807637515735_emit_fdqvvm70q,
      event: 'success',
      displayName: 'emit',
    });
  const act_3601807637515735_reloadGrid_xu247o945 =
    new actionTypes.ReloadGridAction({
      actionArgs: act_3601807637515735_reloadGrid_xu247o945_getActionArgs,
    });
  const inst_evh_3601807637515735_success_5np15bl3f =
    new actionTypes.EventHandlerImpl({
      action: act_3601807637515735_reloadGrid_xu247o945,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_3601807637515735_request_eu649s4m2 = new actionTypes.RequestAction({
    actionArgs: act_3601807637515735_request_eu649s4m2_getActionArgs,
    displayName: 'lock',
    events: [
      inst_evh_3601807637515735_success_9ttk1f1d6,
      inst_evh_3601807637515735_success_5np15bl3f,
    ],
  });
  const inst_evh_3601807637515735_clickPreventStop_gaw73zgvf =
    new actionTypes.EventHandlerImpl({
      action: act_3601807637515735_request_eu649s4m2,
      event: 'click.prevent.stop',
      displayName: 'lock',
    });
  const inst_evh_3601807637515735_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3601807637515735_clickPreventStop_gaw73zgvf],
      event: 'click.prevent.stop',
    });
  const act_4139696446929656_emit_374fwerci = new actionTypes.EmitAction({
    actionArgs: act_4139696446929656_emit_374fwerci_getActionArgs,
  });
  const inst_evh_4139696446929656_success_xogwnksnd =
    new actionTypes.EventHandlerImpl({
      action: act_4139696446929656_emit_374fwerci,
      event: 'success',
      displayName: 'emit',
    });
  const act_4139696446929656_reloadGrid_g3jyxfmoy =
    new actionTypes.ReloadGridAction({
      actionArgs: act_4139696446929656_reloadGrid_g3jyxfmoy_getActionArgs,
    });
  const inst_evh_4139696446929656_success_iwluk88dd =
    new actionTypes.EventHandlerImpl({
      action: act_4139696446929656_reloadGrid_g3jyxfmoy,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_4139696446929656_request_vsyu04aot = new actionTypes.RequestAction({
    actionArgs: act_4139696446929656_request_vsyu04aot_getActionArgs,
    displayName: 'lock',
    events: [
      inst_evh_4139696446929656_success_xogwnksnd,
      inst_evh_4139696446929656_success_iwluk88dd,
    ],
  });
  const inst_evh_4139696446929656_clickPreventStop_s9tbs5yc0 =
    new actionTypes.EventHandlerImpl({
      action: act_4139696446929656_request_vsyu04aot,
      event: 'click.prevent.stop',
      displayName: 'lock',
    });
  const inst_evh_4139696446929656_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4139696446929656_clickPreventStop_s9tbs5yc0],
      event: 'click.prevent.stop',
    });
  const act_4700643919467772_emit_9cp7ht9tm = new actionTypes.EmitAction({
    actionArgs: act_4700643919467772_emit_9cp7ht9tm_getActionArgs,
  });
  const inst_evh_4700643919467772_success_pm7ti5wjy =
    new actionTypes.EventHandlerImpl({
      action: act_4700643919467772_emit_9cp7ht9tm,
      event: 'success',
      displayName: 'emit',
    });
  const act_4700643919467772_reloadGrid_ibky6dfrw =
    new actionTypes.ReloadGridAction({
      actionArgs: act_4700643919467772_reloadGrid_ibky6dfrw_getActionArgs,
    });
  const inst_evh_4700643919467772_success_zhrtkaj4j =
    new actionTypes.EventHandlerImpl({
      action: act_4700643919467772_reloadGrid_ibky6dfrw,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_4700643919467772_request_brv7hyjek = new actionTypes.RequestAction({
    actionArgs: act_4700643919467772_request_brv7hyjek_getActionArgs,
    displayName: 'lock',
    events: [
      inst_evh_4700643919467772_success_pm7ti5wjy,
      inst_evh_4700643919467772_success_zhrtkaj4j,
    ],
  });
  const inst_evh_4700643919467772_clickPreventStop_qdray8khp =
    new actionTypes.EventHandlerImpl({
      action: act_4700643919467772_request_brv7hyjek,
      event: 'click.prevent.stop',
      displayName: 'lock',
    });
  const inst_evh_4700643919467772_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4700643919467772_clickPreventStop_qdray8khp],
      event: 'click.prevent.stop',
    });
  const act_295664594794476_emit_842ors22r = new actionTypes.EmitAction({
    actionArgs: act_295664594794476_emit_842ors22r_getActionArgs,
  });
  const inst_evh_295664594794476_success_5xxibj7pd =
    new actionTypes.EventHandlerImpl({
      action: act_295664594794476_emit_842ors22r,
      event: 'success',
      displayName: 'emit',
    });
  const act_295664594794476_reloadGrid_z1f48osl7 =
    new actionTypes.ReloadGridAction({
      actionArgs: act_295664594794476_reloadGrid_z1f48osl7_getActionArgs,
    });
  const inst_evh_295664594794476_success_6s820ls2s =
    new actionTypes.EventHandlerImpl({
      action: act_295664594794476_reloadGrid_z1f48osl7,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_295664594794476_request_50581xg8r = new actionTypes.RequestAction({
    actionArgs: act_295664594794476_request_50581xg8r_getActionArgs,
    displayName: 'lock',
    events: [
      inst_evh_295664594794476_success_5xxibj7pd,
      inst_evh_295664594794476_success_6s820ls2s,
    ],
  });
  const inst_evh_295664594794476_clickPreventStop_10nmvjxp0 =
    new actionTypes.EventHandlerImpl({
      action: act_295664594794476_request_50581xg8r,
      event: 'click.prevent.stop',
      displayName: 'lock',
    });
  const inst_evh_295664594794476_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_295664594794476_clickPreventStop_10nmvjxp0],
      event: 'click.prevent.stop',
    });
  const act_8474446275270171_emit_oqz2pdo6d = new actionTypes.EmitAction({
    actionArgs: act_8474446275270171_emit_oqz2pdo6d_getActionArgs,
  });
  const inst_evh_8474446275270171_success_vl67gwvxv =
    new actionTypes.EventHandlerImpl({
      action: act_8474446275270171_emit_oqz2pdo6d,
      event: 'success',
      displayName: 'emit',
    });
  const inst_evh_8474446275270171_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8474446275270171_success_vl67gwvxv],
    event: 'success',
  });

  const actions = {
    inst_evh_1040167445267876_clickPreventStop_cxlrqnx6w,
    reload: inst_evh_1040167445267876_clickPreventStop_cxlrqnx6w,
    inst_evh_1040167445267876_clickPreventStop,
    inst_evh_3601807637515735_success_9ttk1f1d6,
    inst_evh_3601807637515735_success_5np15bl3f,
    inst_evh_3601807637515735_clickPreventStop_gaw73zgvf,
    lock: inst_evh_3601807637515735_clickPreventStop_gaw73zgvf,
    inst_evh_3601807637515735_clickPreventStop,
    inst_evh_4139696446929656_success_xogwnksnd,
    inst_evh_4139696446929656_success_iwluk88dd,
    inst_evh_4139696446929656_clickPreventStop_s9tbs5yc0,
    inst_evh_4139696446929656_clickPreventStop,
    inst_evh_4700643919467772_success_pm7ti5wjy,
    inst_evh_4700643919467772_success_zhrtkaj4j,
    inst_evh_4700643919467772_clickPreventStop_qdray8khp,
    inst_evh_4700643919467772_clickPreventStop,
    inst_evh_295664594794476_success_5xxibj7pd,
    inst_evh_295664594794476_success_6s820ls2s,
    inst_evh_295664594794476_clickPreventStop_10nmvjxp0,
    inst_evh_295664594794476_clickPreventStop,
    inst_evh_8474446275270171_success_vl67gwvxv,
    inst_evh_8474446275270171_success,
  };

  const evh_1040167445267876_clickPreventStop_cxlrqnx6w = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1040167445267876_clickPreventStop_cxlrqnx6w.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3601807637515735_success_9ttk1f1d6 = async (event, scope) => {
    return await actions.inst_evh_3601807637515735_success_9ttk1f1d6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3601807637515735_success_5np15bl3f = async (event, scope) => {
    return await actions.inst_evh_3601807637515735_success_5np15bl3f.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3601807637515735_clickPreventStop_gaw73zgvf = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3601807637515735_clickPreventStop_gaw73zgvf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3601807637515735_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3601807637515735_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4139696446929656_success_xogwnksnd = async (event, scope) => {
    return await actions.inst_evh_4139696446929656_success_xogwnksnd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4139696446929656_success_iwluk88dd = async (event, scope) => {
    return await actions.inst_evh_4139696446929656_success_iwluk88dd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4139696446929656_clickPreventStop_s9tbs5yc0 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4139696446929656_clickPreventStop_s9tbs5yc0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4139696446929656_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4139696446929656_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4700643919467772_success_pm7ti5wjy = async (event, scope) => {
    return await actions.inst_evh_4700643919467772_success_pm7ti5wjy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4700643919467772_success_zhrtkaj4j = async (event, scope) => {
    return await actions.inst_evh_4700643919467772_success_zhrtkaj4j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4700643919467772_clickPreventStop_qdray8khp = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4700643919467772_clickPreventStop_qdray8khp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4700643919467772_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4700643919467772_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_295664594794476_success_5xxibj7pd = async (event, scope) => {
    return await actions.inst_evh_295664594794476_success_5xxibj7pd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_295664594794476_success_6s820ls2s = async (event, scope) => {
    return await actions.inst_evh_295664594794476_success_6s820ls2s.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_295664594794476_clickPreventStop_10nmvjxp0 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_295664594794476_clickPreventStop_10nmvjxp0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_295664594794476_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_295664594794476_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8474446275270171_success_vl67gwvxv = async (event, scope) => {
    return await actions.inst_evh_8474446275270171_success_vl67gwvxv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8474446275270171_success = async (event, scope) => {
    return await actions.inst_evh_8474446275270171_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox alignItems="center" class="items-spacing w-100" v-if="loaded">
    <ItpBox direction="column">
      <ItpText muted :text="resourceTitle" class="mb-0" v-if="resourceTitle">
      </ItpText>
      <ItpText tag="h5" :text="resourceKey" class="mb-0"> </ItpText>
    </ItpBox>
    <BButtonGroup class="ms-auto">
      <ItpButton
        icon="fa-sync-alt"
        :tooltip="gettext('Reload')"
        :spinning="actions.reload.isRunning"
        variant="light"
        class="ms-auto"
        v-if="!item.isNew"
        @click.prevent.stop="evh_1040167445267876_clickPreventStop($event, {})"
      >
      </ItpButton>
      <BDropdown right variant="light" class="border" v-if="!item.isNew">
        <template #button-content>
          <ItpIcon fa="bars" class="mx-1"> </ItpIcon>
        </template>
        <ItpButton
          dropdown-item
          :text="pgettext('BtnLockText', 'Lock')"
          :tooltip="pgettext('BtnLockTooltip', 'Lock')"
          variant="default"
          v-if="!item.isNew && item.meta && item.meta.can_lock"
          @click.prevent.stop="
            evh_3601807637515735_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          dropdown-item
          :text="pgettext('BtnLockText', 'Unlock')"
          :tooltip="pgettext('BtnLockTooltip', 'Unlock')"
          variant="default"
          v-if="!item.isNew && item.meta && item.meta.can_unlock"
          @click.prevent.stop="
            evh_4139696446929656_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          dropdown-item
          :text="pgettext('BtnLockText', 'Invalidate')"
          :tooltip="pgettext('BtnLockTooltip', 'Invalidate')"
          variant="default"
          v-if="!item.isNew && item.meta && item.meta.can_invalidate"
          @click.prevent.stop="
            evh_4700643919467772_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          dropdown-item
          :text="pgettext('BtnLockText', 'Revoke invalidated')"
          :tooltip="pgettext('BtnLockTooltip', 'Revoke invalidated')"
          variant="default"
          v-if="!item.isNew && item.meta && item.meta.can_revoke_invalidation"
          @click.prevent.stop="evh_295664594794476_clickPreventStop($event, {})"
        >
        </ItpButton>
        <BtnDeleteResource
          no-icon
          dropdown-item
          :resource="item"
          :tooltip="pgettext('Resource|Command', 'Delete')"
          v-if="!item.isNew && !item.isReadOnly"
          @success="evh_8474446275270171_success($event, {})"
        >
        </BtnDeleteResource>
      </BDropdown>
    </BButtonGroup>
  </ItpBox>
</template>
